import React, { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MapContainer from 'components/Map/MapContainer';
import { getLocationAddress } from 'utils/GeoCoder';
import { isEmpty } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import TourPanel from 'components/FloatingPanels/Tours/TourPanel';
import QRCodePanel from 'components/FloatingPanels/QRCode/QRCodePanel';
import PanelAction from 'components/FloatingPanels/PanelAction/PanelAction';
import { useCookies } from 'react-cookie';
import Menu from 'components/FloatingPanels/Menu/Menu';
import {
  TIMEOUT_VALUES,
  deleteSolution,
  getOAuth,
  setError,
  setUserParam,
} from 'actions';
import getUserLocation from 'utils/getUserLocation';
import TopBar from 'components/TopBar/TopBar';
import { isMobileDevice, isProdEnv } from 'utils/helpers';
import FeedbackContainer from 'components/FloatingPanels/Feedback/FeedbackContainer';
import SolverConfigurationContainer from 'components/FloatingPanels/SolverConfiguration/SolverConfigurationContainer';
import ApiKeyPanel from 'components/FloatingPanels/ApiKey/ApiKeyPanel';
import RoutingAIChat from 'components/Chat/RoutingAIChat';
import withTranslation from 'hoc/withTranslation';
import DevConsole from 'components/FloatingPanels/DevConsole/DevConsole';
import MapSettingsPanel from 'components/FloatingPanels/MapSettings/MapSettingsPanel';
import Footer from 'components/Map/Footer';
import { APP_MODES, getAppMode } from 'utils/urlHelpers';
import usePostMessage from 'hooks/usePostMessage';

const appMode = getAppMode();
const hereMaps = appMode.includes(APP_MODES.HERE_MAPS);
const isProd = isProdEnv();
const showAiChat = !isProd && appMode.includes(APP_MODES.AI_CHAT);

const DAY_COUNT = 3;
const THREE_DAYS = 1000 * 60 * 60 * 24 * DAY_COUNT;

type HomeState = {
  oAuth: unknown;
  user: Record<string, unknown>;
  solution: {
    requests: Array<{ date: string }>;
  };
  error: unknown;
};

type LegacyComponentProps = Record<string, unknown>;

const asLegacyComponent = (component: unknown) =>
  component as React.ComponentType<LegacyComponentProps>;

const PageTourPanel = asLegacyComponent(TourPanel);
const PageQRCodePanel = asLegacyComponent(QRCodePanel);
const PagePanelAction = asLegacyComponent(PanelAction);
const PageMenu = asLegacyComponent(Menu);
const PageTopBar = asLegacyComponent(TopBar);
const PageSolverConfigurationContainer = asLegacyComponent(SolverConfigurationContainer);
const PageMapSettingsPanel = asLegacyComponent(MapSettingsPanel);
const PageApiKeyPanel = asLegacyComponent(ApiKeyPanel);
const PageDevConsole = asLegacyComponent(DevConsole);
const PageFooter = asLegacyComponent(Footer);
const PageRoutingAIChat = asLegacyComponent(RoutingAIChat);

const HomeView = ({ translations }) => {
  const dispatch = useDispatch();
  const [cookies] = useCookies(['apikey']);
  const oAuth = useSelector<HomeState, HomeState['oAuth']>(
    ({ oAuth: stateOAuth }) => stateOAuth,
  );
  const user = useSelector<HomeState, HomeState['user']>(({ user: stateUser }) => stateUser);
  const solution = useSelector<HomeState, HomeState['solution']>(
    ({ solution: stateSolution }) => stateSolution,
  );
  const errorState = useSelector<HomeState, HomeState['error']>(({ error }) => error);
  const handleGetOAuth = useCallback(() => dispatch(getOAuth()), [dispatch]);
  const handleSetUserLocation = useCallback(
    (location) => dispatch(setUserParam({ location })),
    [dispatch],
  );
  const handleSetUserParam = useCallback((data) => dispatch(setUserParam(data)), [dispatch]);
  const handleDeleteSolution = useCallback((idx) => dispatch(deleteSolution(idx)), [dispatch]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMapMobile, setShowMapMobile] = useState(false);
  const [isMenuActive, setIsMenuActive] = useState(true);
  const isMobile = isMobileDevice();
  const [isTourPanelOpen, setIsTourPanelOpen] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [wizardShown, setWizardShown] = useState(true);
  const [externalJsonSession, setExternalJsonSession] = useState(null);

  const handleOpenExternalJsonSession = useCallback(
    (session) => {
      setExternalJsonSession({ ...session, isOpen: true });
      dispatch(setUserParam({ jsonPanelOpen: true }));
    },
    [dispatch],
  );

  const handleExternalJsonDraftChange = useCallback((draftJson) => {
    setExternalJsonSession((current) => (current ? { ...current, draftJson } : current));
  }, []);

  const handleExternalJsonApply = useCallback(
    async (draftJson = externalJsonSession?.draftJson) => {
      if (!externalJsonSession?.onApply) return false;
      const applied = await externalJsonSession.onApply(draftJson);
      if (applied) setExternalJsonSession(null);
      return applied;
    },
    [externalJsonSession],
  );

  const handleExternalJsonClose = useCallback(() => {
    setExternalJsonSession((current) => (current ? { ...current, isOpen: false } : null));
    dispatch(setUserParam({ jsonPanelOpen: false, jsonTourPanelOpen: false }));
  }, [dispatch]);

  const handleExternalJsonReset = useCallback(() => {
    setExternalJsonSession(null);
    dispatch(setUserParam({ jsonPanelOpen: false, jsonTourPanelOpen: false }));
  }, [dispatch]);

  const handleExternalJsonOpen = useCallback(() => {
    setExternalJsonSession((current) => (current ? { ...current, isOpen: true } : current));
    dispatch(setUserParam({ jsonPanelOpen: true }));
  }, [dispatch]);

  usePostMessage({ user, oAuth, solution, translations, cookies });

  const requestDates =
    solution.requests.map((r, idx) => {
      return { date: Date.parse(r.date), idx };
    }) ?? [];

  useEffect(() => {
    const currentTimeAsMs = Date.now();
    if (requestDates) {
      const indexes = [];
      requestDates.forEach((rd) => {
        if (currentTimeAsMs >= rd.date + THREE_DAYS) {
          indexes.push(rd.idx);
        }
      });
      if (!isEmpty(indexes)) handleDeleteSolution({ index: indexes });
    }
  }, [handleDeleteSolution]);

  const handleResize = useCallback(() => {
    setIsMenuActive(false);
    setTimeout(() => setIsMenuActive(true), TIMEOUT_VALUES.HIGH);
  }, [setIsMenuActive]);

  useEffect(() => {
    handleGetOAuth();
    window.addEventListener('resize', handleResize);
  }, [handleGetOAuth, handleResize]);

  useEffect(() => {
    getUserLocation().then(
      (loc) => {
        if (loc && loc.coords) {
          handleSetUserLocation(loc.coords);
          if (oAuth) {
            const location = { lat: loc.coords.latitude, lng: loc.coords.longitude };
            getLocationAddress(location, oAuth, user).then((item) => {
              if (item.address)
                handleSetUserParam({
                  politicalView: item.address.countryCode.toUpperCase(),
                });
            });
          }
        }
      },
      () => {},
    );
  }, [handleSetUserLocation, oAuth, handleSetUserParam]);

  const onToggleMobileView = useCallback(() => {
    setShowMapMobile(!showMapMobile);
  }, [setShowMapMobile, showMapMobile]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleTourPanel = useCallback(() => {
    setIsTourPanelOpen(!isTourPanelOpen);
  }, [isTourPanelOpen]);

  const handleError = useCallback(
    (error) => {
      if (retryCount > 3) return;
      setRetryCount(retryCount + 1);
      if (isProd) dispatch(setError(translations.error.unknownError));
      else dispatch(setError(`${error.name}\n${error.message}\n${error.stack}`));
    },
    [setRetryCount, retryCount, dispatch, translations.error.unknownError],
  );

  return (
    <>
      <ErrorBoundary fallback={null} onError={handleError} resetKeys={[retryCount]}>
        <PageTopBar
          onClickMenu={toggleMenu}
          onToggleMobileView={onToggleMobileView}
          isVisible={!showMapMobile}
          onClickTourPanel={toggleTourPanel}
          isTourPanelOpen={isTourPanelOpen}
          showAiChat={showAiChat}
          solution={solution}
        />
        <MapContainer
          user={user}
          oAuth={oAuth}
          showMapMobile={showMapMobile}
          solution={solution}
          wizardShown={wizardShown}
        />
        {!isMobile && !hereMaps && <PageFooter />}
        {showAiChat && (
          <PageRoutingAIChat
            onOpenExternalJsonSession={handleOpenExternalJsonSession}
            onCloseExternalJsonSession={handleExternalJsonReset}
          />
        )}
        <FeedbackContainer wizardShown={wizardShown} />
        <PageSolverConfigurationContainer user={user} wizardShown={wizardShown} />
        <PageMapSettingsPanel user={user} wizardShown={wizardShown} />
        <PagePanelAction setWizardShown={setWizardShown} wizardShown={wizardShown} />
        <PageQRCodePanel user={user} />
        {!isMobile && (
          <PageDevConsole
            oAuth={oAuth}
            errorState={errorState}
            solution={solution}
            externalJsonSession={externalJsonSession}
            onExternalJsonDraftChange={handleExternalJsonDraftChange}
            onExternalJsonApply={handleExternalJsonApply}
            onExternalJsonClose={handleExternalJsonClose}
            onExternalJsonOpen={handleExternalJsonOpen}
          />
        )}
        {isProd && <PageApiKeyPanel user={user} setIsMenuOpen={(value) => setIsMenuOpen(value)} />}
        {isMenuActive && (
          <PageMenu user={user} isOpen={isMenuOpen} onClickClose={toggleMenu} solution={solution} />
        )}
        <PageTourPanel solution={solution} user={user} oAuth={oAuth} isOpen={isTourPanelOpen} />
      </ErrorBoundary>
    </>
  );
};

export default withTranslation(HomeView);
